import React, { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext, API } from "../App";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Skeleton } from "./ui/skeleton";
import { Badge } from "./ui/badge";
import { Save, Plus, Trash2, CloudSun, Users, ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { toast } from "sonner";
import { format, addDays, subDays } from "date-fns";
import { DiaryPhotos } from "./photos/DiaryPhotos";



export default function CompactDailyForm() {
  const { user } = useContext(AuthContext);
  
  // Check if user is view_only (read-only mode)
  const isViewOnly = user?.role === "view_only";
  
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sites, setSites] = useState([]);
  const [trades, setTrades] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [siteManagers, setSiteManagers] = useState([]);
  const [existingRecordId, setExistingRecordId] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [hasRecord, setHasRecord] = useState(true); // Track if current date has a record
  
  // Quick add dialogs
  const [showAddTradeDialog, setShowAddTradeDialog] = useState(false);
  const [showAddCompanyDialog, setShowAddCompanyDialog] = useState(false);
  const [showAddManagerDialog, setShowAddManagerDialog] = useState(false);
  const [showAddTaskDialog, setShowAddTaskDialog] = useState(false);
  const [newTradeName, setNewTradeName] = useState("");
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newManager, setNewManager] = useState({
    username: "",
    email: "",
    password: ""
  });
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    scheduled_date: "",
    priority: "medium",
    assigned_to: []
  });
  const [addingItem, setAddingItem] = useState(false);
  
  // Common equipment list for suggestions
  const commonEquipment = [
    "Tower Crane",
    "Mobile Crane",
    "Excavator",
    "Bulldozer",
    "Backhoe Loader",
    "Forklift",
    "Telehandler",
    "Boom Lift",
    "Scissor Lift",
    "Concrete Mixer",
    "Concrete Pump",
    "Compactor/Roller",
    "Generator",
    "Welding Machine",
    "Air Compressor",
    "Scaffolding",
    "Dumper Truck",
    "Grader",
    "Paver",
    "Drill Rig"
  ];
  
  // Form state
  const [formData, setFormData] = useState({
    site_id: location.state?.siteId || "",
    site_manager_ids: [],  // Selected managers for this record
    date: location.state?.selectedDate || format(new Date(), "yyyy-MM-dd"),
    weather_condition: "moderate",
    temperature: "",
    lost_day: false,
    weather_comment: "",
    
    // Subcontractors (Work Details Contractors)
    subcontractors: [{ company: "", number_of_persons: "", activity_overview: "" }],
    
    // Day Labour
    day_labour: [{ trade: "", number_of_persons: "", number_of: "", start_time: "07:00", end_time: "16:00", total_hours: "", activity_summary: "" }],
    
    // Equipment
    equipment: [{ name: "", trade: "", comments: "" }],
    
    // Materials/Deliveries
    deliveries: [{ description: "", supplier: "", comments: "" }],
    
    // Visitors
    visitors: [{ name: "", company: "", purpose: "" }],
    
    // No Shows - Subcontractors who didn't show up
    no_shows: [{ company: "", description: "" }],
    
    // Significant Events (Achievements)
    significant_events: ""
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  // Load existing record when site or date changes
  useEffect(() => {
    if (formData.site_id && formData.date) {
      loadExistingRecord(formData.site_id, formData.date);
    }
  }, [formData.site_id, formData.date]);

  // Update site managers when site selection changes
  useEffect(() => {
    if (formData.site_id && sites.length > 0) {
      const selectedSiteData = sites.find(s => s.id === formData.site_id);
      
      const expectedManagerIds = selectedSiteData?.site_manager_ids || [];
      const currentManagerIds = formData.site_manager_ids || [];
      
      // Compare arrays to see if we need to update
      const needsUpdate = 
        expectedManagerIds.length !== currentManagerIds.length ||
        !expectedManagerIds.every(id => currentManagerIds.includes(id));
      
      console.log('🔍 Site selection check:', {
        siteId: formData.site_id,
        siteName: selectedSiteData?.name,
        expectedManagerIds,
        currentManagerIds,
        needsUpdate
      });
      
      if (needsUpdate) {
        console.log('✅ Updating site managers to:', expectedManagerIds);
        setFormData(prev => ({
          ...prev,
          site_manager_ids: expectedManagerIds
        }));
      }
    }
  }, [formData.site_id, sites, formData.site_manager_ids]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // Load sites
      const sitesRes = await fetch(`${API}/sites`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (sitesRes.ok) {
        const sitesData = await sitesRes.json();
        setSites(sitesData);
        // Don't auto-select first site - let user choose
      }
      
      // Load trades
      const tradesRes = await fetch(`${API}/trades?active_only=true`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (tradesRes.ok) {
        const tradesData = await tradesRes.json();
        setTrades(tradesData);
      }
      
      // Load companies
      const companiesRes = await fetch(`${API}/companies?active_only=true`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (companiesRes.ok) {
        const companiesData = await companiesRes.json();
        setCompanies(companiesData);
      }
      
      // Load site managers
      const managersRes = await fetch(`${API}/users/site-managers`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (managersRes.ok) {
        const managersData = await managersRes.json();
        setSiteManagers(managersData);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load form data");
    } finally {
      setLoading(false);
    }
  };

  const loadExistingRecord = async (siteId, date) => {
    if (!siteId || !date) return;
    
    setLoadingRecord(true);
    try {
      const token = localStorage.getItem("token");
      
      // Fetch daily records for this site and date (using start_date and end_date with same value)
      const response = await fetch(`${API}/daily-records?site_id=${siteId}&start_date=${date}&end_date=${date}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const records = await response.json();
        
        if (records && records.length > 0) {
          const record = records[0]; // Get the first matching record
          
          setHasRecord(true); // Record exists
          
          // Populate form with existing data
          setExistingRecordId(record.id);
          
          // Get site managers from the site
          const siteDoc = sites.find(s => s.id === record.site_id);
          const siteManagerIds = siteDoc?.site_manager_ids || [];
          
          setFormData(prev => ({
            ...prev,
            site_id: record.site_id,
            site_manager_ids: siteManagerIds,
            date: record.date,
            weather_condition: record.weather_condition || "moderate",
            temperature: record.temperature?.toString() || "",
            lost_day: record.lost_day || false,
            weather_comment: record.weather_comment || "",
            
            // Subcontractors
            subcontractors: record.subcontractors && record.subcontractors.length > 0
              ? record.subcontractors.map(s => ({
                  company: s.company || "",
                  number_of_persons: s.number_of_persons?.toString() || "",
                  activity_overview: s.activity_overview || ""
                }))
              : [{ company: "", number_of_persons: "", activity_overview: "" }],
            
            // Day Labour
            day_labour: record.day_labour && record.day_labour.length > 0
              ? record.day_labour.map(d => ({
                  trade: d.trade || "",
                  number_of_persons: d.number_of_persons?.toString() || d.number_of?.toString() || "",
                  number_of: d.number_of?.toString() || d.number_of_persons?.toString() || "",
                  start_time: d.start_time?.substring(0, 5) || "07:00",
                  end_time: d.end_time?.substring(0, 5) || "16:00",
                  total_hours: d.total_hours?.toString() || "",
                  activity_summary: d.activity_summary || ""
                }))
              : [{ trade: "", number_of_persons: "", number_of: "", start_time: "07:00", end_time: "16:00", total_hours: "", activity_summary: "" }],
            
            // Equipment
            equipment: record.equipment && record.equipment.length > 0
              ? record.equipment.map(e => ({
                  name: e.name || "",
                  trade: e.type || "",
                  comments: e.status || ""
                }))
              : [{ name: "", trade: "", comments: "" }],
            
            // Deliveries
            deliveries: record.deliveries && record.deliveries.length > 0
              ? record.deliveries.map(d => ({
                  description: d.description || "",
                  supplier: d.supplier || "",
                  comments: ""
                }))
              : [{ description: "", supplier: "", comments: "" }],
            
            // Visitors
            visitors: record.visitors && record.visitors.length > 0
              ? record.visitors.map(v => ({
                  name: v.name || "",
                  company: v.company || "",
                  purpose: v.purpose || ""
                }))
              : [{ name: "", company: "", purpose: "" }],
            
            // No Shows
            no_shows: record.no_shows && record.no_shows.length > 0
              ? record.no_shows.map(n => ({
                  company: n.company || "",
                  description: n.description || ""
                }))
              : [{ company: "", description: "" }],
            
            // Significant Events
            significant_events: record.main_achievements || ""
          }));
          
          toast.info(`Loaded existing record for ${date}. You can edit and update it.`);
        } else {
          // No existing record, reset to defaults but keep site and date
          setHasRecord(false); // No record exists
          setExistingRecordId(null);
          resetFormKeepSiteDate(siteId, date);
        }
      }
    } catch (error) {
      console.error("Error loading existing record:", error);
      // Don't show error toast, just reset form
      setHasRecord(false); // Assume no record on error
      setExistingRecordId(null);
      resetFormKeepSiteDate(siteId, date);
    } finally {
      setLoadingRecord(false);
    }
  };

  const resetFormKeepSiteDate = (siteId, date) => {
    setFormData({
      site_id: siteId,
      site_manager_ids: [],
      date: date,
      weather_condition: "moderate",
      temperature: "",
      lost_day: false,
      weather_comment: "",
      subcontractors: [{ company: "", number_of_persons: "", activity_overview: "" }],
      day_labour: [{ trade: "", number_of_persons: "", number_of: "", start_time: "07:00", end_time: "16:00", total_hours: "", activity_summary: "" }],
      equipment: [{ name: "", trade: "", comments: "" }],
      deliveries: [{ description: "", supplier: "", comments: "" }],
      visitors: [{ name: "", company: "", purpose: "" }],
      no_shows: [{ company: "", description: "" }],
      significant_events: ""
    });
  };

  // Date navigation functions
  const navigateDate = async (direction) => {
    if (!formData.site_id) {
      toast.error("Please select a site first");
      return;
    }

    const currentDate = new Date(formData.date);
    const newDate = direction === 'prev' ? subDays(currentDate, 1) : addDays(currentDate, 1);
    const newDateStr = format(newDate, "yyyy-MM-dd");
    
    // Always navigate to the new date
    setFormData(prev => ({ ...prev, date: newDateStr }));
    // Load existing record if available (will set hasRecord state accordingly)
    await loadExistingRecord(formData.site_id, newDateStr);
  };

  // Delete daily record
  const handleDeleteRecord = async () => {
    if (!existingRecordId) {
      toast.error("No record to delete");
      return;
    }

    // Confirmation dialog
    if (!window.confirm("Are you sure you want to delete this daily record? This action cannot be undone. Note: Any tasks/action items will remain in the Calendar.")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/daily-records/${existingRecordId}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success("Daily record deleted successfully");
        // Reset form but keep site and date
        resetFormKeepSiteDate(formData.site_id, formData.date);
        setHasRecord(false);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to delete record");
      }
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record");
    }
  };

  // Generate PDF for daily record using backend
  const handlePrintPDF = async () => {
    if (!existingRecordId) {
      toast.error("No record to print. Please save the record first.");
      return;
    }

    try {
      toast.info("Generating PDF...");
      const token = localStorage.getItem("token");
      
      const response = await fetch(`${API}/daily-records/${existingRecordId}/pdf`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Generate filename with site name and date
        const siteName = selectedSite?.name || 'Site';
        const dateStr = format(new Date(formData.date), 'dd-MM-yyyy');
        a.download = `DailyRecord_${siteName}_${dateStr}.pdf`;
        
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success("PDF generated successfully!");
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to generate PDF");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  // Helper functions for array fields
  const addArrayItem = (field, defaultItem) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], defaultItem]
    }));
  };

  const updateArrayItem = (field, index, key, value) => {
    // Validation: Check for contractor conflicts between subcontractors and no_shows
    if ((field === "subcontractors" || field === "no_shows") && key === "company" && value) {
      const otherField = field === "subcontractors" ? "no_shows" : "subcontractors";
      const otherList = formData[otherField];
      
      // Check if company already exists in the other field
      const existsInOther = otherList.some(item => item.company === value);
      
      if (existsInOther) {
        const fieldName = field === "subcontractors" ? "Work Details Contractors" : "No Shows";
        const otherFieldName = otherField === "subcontractors" ? "Work Details Contractors" : "No Shows";
        toast.error(`${value} is already in ${otherFieldName}. A contractor cannot be in both sections.`);
        return; // Don't update if conflict exists
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => 
        i === index ? { ...item, [key]: value } : item
      )
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Calculate hours for day labour
  const calculateHours = (start, end) => {
    if (!start || !end) return "";
    const [startH, startM] = start.split(":").map(Number);
    const [endH, endM] = end.split(":").map(Number);
    const hours = (endH + endM / 60) - (startH + startM / 60);
    return hours > 0 ? hours.toFixed(1) : "";
  };

  const handleDayLabourTimeChange = (index, field, value) => {
    updateArrayItem("day_labour", index, field, value);
    
    // Auto-calculate hours
    const item = formData.day_labour[index];
    const start = field === "start_time" ? value : item.start_time;
    const end = field === "end_time" ? value : item.end_time;
    const hours = calculateHours(start, end);
    if (hours) {
      updateArrayItem("day_labour", index, "total_hours", hours);
    }
  };

  const handleAddTrade = async () => {
    if (!newTradeName.trim()) {
      toast.error("Trade name is required");
      return;
    }

    setAddingItem(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/trades`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: newTradeName.trim(),
          description: "",
          category: "general",
          is_active: true
        })
      });

      if (response.ok) {
        const newTrade = await response.json();
        setTrades(prev => [...prev, newTrade]);
        toast.success(`Trade "${newTradeName}" added successfully!`);
        setNewTradeName("");
        setShowAddTradeDialog(false);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to add trade");
      }
    } catch (error) {
      console.error("Error adding trade:", error);
      toast.error("Failed to add trade");
    } finally {
      setAddingItem(false);
    }
  };

  const handleAddCompany = async () => {
    if (!newCompanyName.trim()) {
      toast.error("Subcontractor name is required");
      return;
    }

    if (!formData.site_id) {
      toast.error("Please select a site first so the subcontractor can be allocated to it");
      return;
    }

    setAddingItem(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/companies`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: newCompanyName.trim(),
          contact_person: "",
          phone: "",
          email: "",
          address: "",
          company_type: "subcontractor",
          site_ids: [formData.site_id],
          is_active: true
        })
      });

      if (response.ok) {
        const newCompany = await response.json();
        setCompanies(prev => [...prev, newCompany]);
        toast.success(`Subcontractor "${newCompanyName}" added to this site!`);
        setNewCompanyName("");
        setShowAddCompanyDialog(false);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to add subcontractor");
      }
    } catch (error) {
      console.error("Error adding subcontractor:", error);
      toast.error("Failed to add subcontractor");
    } finally {
      setAddingItem(false);
    }
  };

  const handleAddSiteManager = async () => {
    if (!newManager.username.trim() || !newManager.email.trim() || !newManager.password.trim()) {
      toast.error("All fields are required");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newManager.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setAddingItem(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          username: newManager.username,
          email: newManager.email,
          password: newManager.password,
          role: "site_manager"
        })
      });

      if (response.ok) {
        const createdManager = await response.json();
        setSiteManagers(prev => [...prev, createdManager.user]);
        toast.success(`Site Manager "${newManager.username}" added successfully!`);
        setNewManager({ username: "", email: "", password: "" });
        setShowAddManagerDialog(false);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to add site manager");
      }
    } catch (error) {
      console.error("Error adding site manager:", error);
      toast.error("Failed to add site manager");
    } finally {
      setAddingItem(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.site_id) {
      toast.error("Please select a site");
      return;
    }

    setSaving(true);
    
    try {
      const token = localStorage.getItem("token");
      
      // Prepare data for backend
      const submitData = {
        site_id: formData.site_id,
        date: formData.date,
        weather_condition: formData.weather_condition,
        temperature: parseFloat(formData.temperature) || 0,
        lost_day: formData.lost_day || false,
        weather_comment: formData.weather_comment || "",
        
        // Filter out empty subcontractors
        subcontractors: formData.subcontractors
          .filter(s => s.company && s.number_of_persons)
          .map(s => ({
            company: s.company,
            number_of_persons: parseInt(s.number_of_persons) || 0,
            activity_overview: s.activity_overview || ""
          })),
        
        // Filter out empty day labour
        day_labour: formData.day_labour
          .filter(d => d.trade)
          .map(d => ({
            trade: d.trade,
            company: d.trade, // Company name is stored in trade field
            number_of_persons: parseInt(d.number_of_persons) || parseInt(d.number_of) || 1,
            start_time: d.start_time || "07:00:00",
            end_time: d.end_time || "16:00:00",
            total_hours: parseFloat(d.total_hours) || 8.0,
            number_of: parseInt(d.number_of) || 0,
            activity_summary: d.activity_summary || ""
          })),
        
        // Filter out empty equipment
        equipment: formData.equipment
          .filter(e => e.name)
          .map(e => ({
            name: e.name,
            type: e.trade || "",
            operator: "",
            hours_operated: 0,
            status: e.comments || "operational"
          })),
        
        // Filter out empty deliveries
        deliveries: formData.deliveries
          .filter(d => d.description)
          .map(d => ({
            description: d.description,
            time: format(new Date(), "HH:mm:ss"),
            supplier: d.supplier || ""
          })),
        
        // Filter out empty visitors
        visitors: formData.visitors
          .filter(v => v.name)
          .map(v => ({
            name: v.name,
            company: v.company || "",
            purpose: v.purpose || "",
            time_in: format(new Date(), "HH:mm:ss"),
            time_out: ""
          })),
        
        // Filter out empty no shows
        no_shows: formData.no_shows
          .filter(n => n.company)
          .map(n => ({
            company: n.company,
            description: n.description || ""
          })),
        
        main_achievements: formData.significant_events || "",
        
        // Calculate totals
        total_day_labour: formData.day_labour
          .filter(d => d.trade)
          .reduce((sum, d) => sum + (parseInt(d.number_of) || 0), 0)
      };
      
      // Use PUT if updating existing record, POST if creating new
      const url = existingRecordId 
        ? `${API}/daily-records/${existingRecordId}`
        : `${API}/daily-records`;
      const method = existingRecordId ? "PUT" : "POST";
      
      const response = await fetch(url, {
        method: method,
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        const savedRecord = await response.json();
        setExistingRecordId(savedRecord.id);
        
        // Update site with selected managers if they've changed
        const currentSite = sites.find(s => s.id === formData.site_id);
        const currentManagerIds = currentSite?.site_manager_ids || [];
        const selectedManagerIds = formData.site_manager_ids || [];
        
        // Check if managers have changed
        const managersChanged = 
          currentManagerIds.length !== selectedManagerIds.length ||
          !currentManagerIds.every(id => selectedManagerIds.includes(id));
        
        if (managersChanged) {
          try {
            const siteUpdateResponse = await fetch(`${API}/sites/${formData.site_id}`, {
              method: "PUT",
              headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
              },
              body: JSON.stringify({
                name: currentSite.name,
                address: currentSite.address,
                description: currentSite.description || "",
                site_manager_ids: selectedManagerIds
              })
            });
            
            if (siteUpdateResponse.ok) {
              // Update local sites array
              setSites(prevSites => prevSites.map(site => 
                site.id === formData.site_id 
                  ? { ...site, site_manager_ids: selectedManagerIds }
                  : site
              ));
              console.log("✅ Site managers updated successfully");
            }
          } catch (error) {
            console.error("Error updating site managers:", error);
            // Don't show error to user - daily record was saved successfully
          }
        }
        
        toast.success(existingRecordId ? "Daily record updated successfully!" : "Daily record created successfully!");
        // Don't reset form after save, keep the data for further editing
      } else {
        const errorData = await response.json();
        // Handle Pydantic validation errors (which are arrays)
        if (Array.isArray(errorData.detail)) {
          const errorMessages = errorData.detail.map(err => 
            `${err.loc ? err.loc.join(' -> ') : 'Field'}: ${err.msg}`
          ).join('; ');
          toast.error(`Validation error: ${errorMessages}`);
        } else {
          toast.error(errorData.detail || "Failed to save daily record");
        }
      }
    } catch (error) {
      console.error("Error saving record:", error);
      toast.error("Failed to save daily record");
    } finally {
      setSaving(false);
    }
  };

  const selectedSite = sites.find(s => s.id === formData.site_id);

  if (loading) {
    return (
      <div className="p-4">
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const weatherOptions = [
    { value: "rain_lost_day", label: "Rain (Lost Day)" },
    { value: "rainy", label: "Rainy" },
    { value: "overcast", label: "Overcast" },
    { value: "windy", label: "Windy" },
    { value: "moderate", label: "Moderate" },
    { value: "sunny", label: "Sunny" },
    { value: "temperature_extreme", label: "Temperature Extreme" }
  ];

  return (
    <div className="max-w-[1600px] mx-auto p-2 md:p-4" id="daily-record-form">
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Top Section - Date, Project Details - NOT DISABLED for view_only (navigation) */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          {/* Date */}
          <Card className="border-2 border-slate-700">
            <CardHeader className="bg-slate-700 text-white p-2">
              <CardTitle className="text-xs font-bold text-center">DATE</CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigateDate('prev')}
                  className="h-8 w-8 p-0"
                  title="Previous day"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className={`h-8 text-lg font-bold text-center flex-1 ${!hasRecord ? 'border-red-500 border-2 bg-red-50 text-red-700' : ''}`}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigateDate('next')}
                  className="h-8 w-8 p-0"
                  title="Next day"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Project Details */}
          <Card className="border-2 border-slate-700">
            <CardHeader className="bg-slate-700 text-white p-2">
              <CardTitle className="text-xs font-bold text-center">PROJECT DETAILS</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-2 text-xs">
              <div>
                <Label className="text-xs font-semibold">PROJECT</Label>
                <Select 
                  value={formData.site_id || "___placeholder___"} 
                  onValueChange={(value) => {
                    if (value !== "___placeholder___") {
                      setFormData(prev => ({ ...prev, site_id: value }));
                    }
                  }}
                >
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue placeholder="Select construction site" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map(site => (
                      <SelectItem key={site.id} value={site.id} className="text-xs">
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">SITE MANAGERS</Label>
                <Select 
                  value={formData.site_manager_ids.length > 0 ? "selected" : "none"}
                  onValueChange={(value) => {
                    if (value === "__add_new__") {
                      setShowAddManagerDialog(true);
                    }
                  }}
                >
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue>
                      {(() => {
                        // Get manager names from the selected site
                        const selectedSite = sites.find(s => s.id === formData.site_id);
                        
                        console.log('🎨 Rendering site managers display');
                        console.log('🎨 Selected site:', selectedSite?.name);
                        console.log('🎨 site_manager_names:', selectedSite?.site_manager_names);
                        console.log('🎨 formData.site_manager_ids:', formData.site_manager_ids);
                        
                        if (selectedSite && selectedSite.site_manager_names && selectedSite.site_manager_names.length > 0) {
                          return selectedSite.site_manager_names.join(", ");
                        }
                        
                        // Fallback: check if we have site_manager_ids set
                        if (formData.site_manager_ids.length > 0) {
                          // Try to get names from siteManagers array
                          const selectedManagers = siteManagers
                            .filter(m => formData.site_manager_ids.includes(m.id))
                            .map(m => m.username);
                          
                          console.log('🎨 Looking up managers from siteManagers array:', selectedManagers);
                          
                          if (selectedManagers.length > 0) {
                            return selectedManagers.join(", ");
                          }
                          return "Loading managers...";
                        }
                        
                        return "Select managers";
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <div className="p-2 max-h-64 overflow-y-auto">
                      <div className="mb-2">
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-blue-600 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAddManagerDialog(true);
                          }}
                        >
                          + Add New Manager
                        </Button>
                      </div>
                      {(() => {
                        // Filter managers to only show those assigned to the selected site
                        const selectedSite = sites.find(s => s.id === formData.site_id);
                        const assignedManagerIds = selectedSite?.site_manager_ids || [];
                        const assignedManagers = siteManagers.filter(m => assignedManagerIds.includes(m.id));
                        
                        if (assignedManagers.length === 0) {
                          return <p className="text-xs text-gray-500 p-2">No site managers assigned to this site</p>;
                        }
                        
                        return (
                          <div className="space-y-1">
                            {assignedManagers.map((manager) => (
                              <div
                                key={manager.id}
                                className="flex items-center space-x-2 p-1 hover:bg-slate-50 rounded cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const isSelected = formData.site_manager_ids.includes(manager.id);
                                  if (isSelected) {
                                    setFormData(prev => ({
                                      ...prev,
                                      site_manager_ids: prev.site_manager_ids.filter(id => id !== manager.id)
                                    }));
                                  } else {
                                    setFormData(prev => ({
                                      ...prev,
                                      site_manager_ids: [...prev.site_manager_ids, manager.id]
                                    }));
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.site_manager_ids.includes(manager.id)}
                                  onChange={() => {}}
                                  className="w-3 h-3"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <Label className="text-xs font-normal cursor-pointer flex-1">
                                  {manager.username} <span className="text-gray-500">({manager.role})</span>
                                </Label>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          
          {/* Rest of the form is disabled for view_only users */}
          <fieldset disabled={isViewOnly} className="contents">
          {/* Weather */}
          <Card className="border-2 border-slate-700">
            <CardHeader className="bg-slate-700 text-white p-2">
              <CardTitle className="text-xs font-bold text-center">WEATHER</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <Label className="text-xs">Conditions</Label>
                  <Select value={formData.weather_condition} onValueChange={(value) => setFormData(prev => ({ ...prev, weather_condition: value }))}>
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {weatherOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Temp. °C</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.temperature}
                    onChange={(e) => setFormData(prev => ({ ...prev, temperature: e.target.value }))}
                    className="h-7 text-xs"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="lost-day"
                  checked={formData.lost_day}
                  onChange={(e) => setFormData(prev => ({ ...prev, lost_day: e.target.checked }))}
                  className="w-3 h-3"
                />
                <Label htmlFor="lost-day" className="text-xs">Lost Day y/n</Label>
              </div>
              <div>
                <Label className="text-xs">Comment:</Label>
                <Input
                  value={formData.weather_comment}
                  onChange={(e) => setFormData(prev => ({ ...prev, weather_comment: e.target.value }))}
                  className="h-7 text-xs"
                  placeholder="Weather comments"
                />
              </div>
            </CardContent>
          </Card>

          {/* No Shows */}
          <Card className="border-2 border-red-600">
            <CardHeader className="bg-red-600 text-white p-2 flex flex-row items-center justify-between">
              <CardTitle className="text-xs font-bold text-center">NO SHOWS</CardTitle>
              <Button 
                onClick={() => setFormData(prev => ({
                  ...prev,
                  no_shows: [...prev.no_shows, { company: "", description: "" }]
                }))}
                size="sm"
                variant="ghost"
                className="h-6 text-white hover:bg-red-700 hover:text-white p-1"
              >
                <Plus className="w-3 h-3" />
              </Button>
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1">
                {formData.no_shows.map((noShow, index) => (
                  <div key={index} className="grid grid-cols-12 gap-1 items-center">
                    <div className="col-span-5">
                      <Select 
                        value={noShow.company} 
                        onValueChange={(value) => {
                          if (value === "__add_new__") {
                            setShowAddCompanyDialog(true);
                          } else {
                            updateArrayItem("no_shows", index, "company", value);
                          }
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs">
                          <SelectValue placeholder="Select company" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__add_new__" className="text-xs text-blue-600 font-semibold">
                            + Add New Subcontractor
                          </SelectItem>
                          {companies.filter(c => c.company_type === "subcontractor" || c.company_type === "contractor").map(company => (
                            <SelectItem key={company.id} value={company.name} className="text-xs">
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-6">
                      <Input
                        value={noShow.description || ""}
                        onChange={(e) => updateArrayItem("no_shows", index, "description", e.target.value)}
                        className="h-7 text-xs"
                        placeholder="Reason"
                      />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      {formData.no_shows.length > 0 && !isViewOnly && (
                        <Button
                          onClick={() => removeArrayItem("no_shows", index)}
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 hover:bg-red-100"
                        >
                          <Trash2 className="w-3 h-3 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          </fieldset>
        </div>

        {/* Middle Section - Subcontractors, Equipment, Materials */}
        <fieldset disabled={isViewOnly}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Work Details Contractors (Subcontractors) */}
          <Card className="border-2 border-slate-700">
            <CardHeader className="bg-slate-700 text-white p-2 flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-xs font-bold">WORK DETAILS CONTRACTORS</CardTitle>
                <Badge className="bg-white text-slate-700 text-xs px-2 py-0">
                  Total: {formData.subcontractors.reduce((sum, sub) => sum + (parseInt(sub.number_of_persons) || 0), 0)}
                </Badge>
              </div>
              {!isViewOnly && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 text-white hover:bg-slate-600"
                  onClick={() => addArrayItem("subcontractors", { company: "", number_of_persons: "", activity_overview: "" })}
                >
                  <Plus className="w-3 h-3" />
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1">
                <div className="hidden md:grid grid-cols-12 gap-1 text-xs font-semibold bg-slate-100 p-1">
                  <div className="col-span-3">SUBCONTRACTOR</div>
                  <div className="col-span-2 text-center">NO.</div>
                  <div className="col-span-6">ACTIVITY OVERVIEW</div>
                  <div className="col-span-1"></div>
                </div>
                {formData.subcontractors.map((sub, index) => (
                  <div key={index} className="border border-slate-200 rounded p-2 space-y-2 md:space-y-0 md:grid md:grid-cols-12 md:gap-1 md:items-center md:border-0 md:p-0">
                    <div className="md:col-span-3">
                      <label className="block md:hidden text-xs font-semibold text-slate-600 mb-1">Subcontractor</label>
                      <Select 
                        value={sub.company} 
                        onValueChange={(value) => {
                          if (value === "__add_new__") {
                            setShowAddCompanyDialog(true);
                          } else {
                            updateArrayItem("subcontractors", index, "company", value);
                          }
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__add_new__" className="text-xs text-blue-600 font-semibold">
                            + Add New Subcontractor
                          </SelectItem>
                          {companies.filter(c => c.company_type === "subcontractor" || c.company_type === "contractor").map(company => (
                            <SelectItem key={company.id} value={company.name} className="text-xs">
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block md:hidden text-xs font-semibold text-slate-600 mb-1">No. of Persons</label>
                      <Input
                        type="number"
                        value={sub.number_of_persons}
                        onChange={(e) => updateArrayItem("subcontractors", index, "number_of_persons", e.target.value)}
                        className="h-7 text-xs text-center"
                      />
                    </div>
                    <div className="md:col-span-6">
                      <label className="block md:hidden text-xs font-semibold text-slate-600 mb-1">Activity Overview</label>
                      <Input
                        value={sub.activity_overview}
                        onChange={(e) => updateArrayItem("subcontractors", index, "activity_overview", e.target.value)}
                        className="h-7 text-xs"
                        placeholder="Activity description"
                      />
                    </div>
                    <div className="md:col-span-1 flex justify-end md:justify-center">
                      {formData.subcontractors.length > 0 && !isViewOnly && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={() => removeArrayItem("subcontractors", index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Equipment and Materials */}
          <div className="space-y-3">
            {/* Equipment */}
            <Card className="border-2 border-slate-700">
              <CardHeader className="bg-slate-700 text-white p-2 flex flex-row items-center justify-between">
                <CardTitle className="text-xs font-bold">ON SITE EQUIPMENT</CardTitle>
                {!isViewOnly && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 text-white hover:bg-slate-600"
                    onClick={() => addArrayItem("equipment", { name: "", trade: "", comments: "" })}
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                )}
              </CardHeader>
              <CardContent className="p-2">
                <div className="space-y-1">
                  <div className="grid grid-cols-12 gap-1 text-xs font-semibold bg-slate-100 p-1">
                    <div className="col-span-4">EQUIPMENT</div>
                    <div className="col-span-3">TRADE</div>
                    <div className="col-span-4">COMMENTS</div>
                    <div className="col-span-1"></div>
                  </div>
                  {formData.equipment.map((equip, index) => (
                    <div key={index} className="grid grid-cols-12 gap-1 items-center">
                      <div className="col-span-4">
                        <Input
                          list={`equipment-list-${index}`}
                          value={equip.name}
                          onChange={(e) => updateArrayItem("equipment", index, "name", e.target.value)}
                          className="h-7 text-xs"
                          placeholder="Select or type equipment"
                        />
                        <datalist id={`equipment-list-${index}`}>
                          {commonEquipment.map((item, idx) => (
                            <option key={idx} value={item} />
                          ))}
                        </datalist>
                      </div>
                      <div className="col-span-3">
                        <Select 
                          value={equip.trade} 
                          onValueChange={(value) => {
                            if (value === "__add_new__") {
                              setShowAddTradeDialog(true);
                            } else {
                              updateArrayItem("equipment", index, "trade", value);
                            }
                          }}
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue placeholder="Select Trade" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__add_new__" className="text-xs text-blue-600 font-semibold">
                              + Add New Trade
                            </SelectItem>
                            {trades.map(trade => (
                              <SelectItem key={trade.id} value={trade.name} className="text-xs">
                                {trade.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4">
                        <Input
                          value={equip.comments}
                          onChange={(e) => updateArrayItem("equipment", index, "comments", e.target.value)}
                          className="h-7 text-xs"
                          placeholder="Comments"
                        />
                      </div>
                      <div className="col-span-1">
                        {formData.equipment.length > 0 && !isViewOnly && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => removeArrayItem("equipment", index)}
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Materials Received */}
            <Card className="border-2 border-slate-700">
              <CardHeader className="bg-slate-700 text-white p-2 flex flex-row items-center justify-between">
                <CardTitle className="text-xs font-bold">MATERIALS RECEIVED</CardTitle>
                {!isViewOnly && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 text-white hover:bg-slate-600"
                    onClick={() => addArrayItem("deliveries", { description: "", supplier: "", comments: "" })}
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                )}
              </CardHeader>
              <CardContent className="p-2">
                <div className="space-y-1">
                  <div className="grid grid-cols-12 gap-1 text-xs font-semibold bg-slate-100 p-1">
                    <div className="col-span-4">DESCRIPTION</div>
                    <div className="col-span-3">SUPPLIER</div>
                    <div className="col-span-4">COMMENTS</div>
                    <div className="col-span-1"></div>
                  </div>
                  {formData.deliveries.map((delivery, index) => (
                    <div key={index} className="grid grid-cols-12 gap-1 items-center">
                      <div className="col-span-4">
                        <Input
                          value={delivery.description}
                          onChange={(e) => updateArrayItem("deliveries", index, "description", e.target.value)}
                          className="h-7 text-xs"
                          placeholder="Material description"
                        />
                      </div>
                      <div className="col-span-3">
                        <Input
                          value={delivery.supplier}
                          onChange={(e) => updateArrayItem("deliveries", index, "supplier", e.target.value)}
                          className="h-7 text-xs"
                          placeholder="Supplier"
                        />
                      </div>
                      <div className="col-span-4">
                        <Input
                          value={delivery.comments}
                          onChange={(e) => updateArrayItem("deliveries", index, "comments", e.target.value)}
                          className="h-7 text-xs"
                          placeholder="Comments"
                        />
                      </div>
                      <div className="col-span-1">
                        {formData.deliveries.length > 0 && !isViewOnly && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => removeArrayItem("deliveries", index)}
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bottom Section - Day Labour, Visitors, Significant Events */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Day Labour */}
          <Card className="border-2 border-slate-700">
            <CardHeader className="bg-slate-700 text-white p-2 flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-xs font-bold">DAY LABOUR</CardTitle>
                <Badge className="bg-white text-slate-700 text-xs px-2 py-0">
                  Total: {formData.day_labour.reduce((sum, labour) => sum + (parseInt(labour.number_of) || 0), 0)}
                </Badge>
              </div>
              {!isViewOnly && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 text-white hover:bg-slate-600"
                  onClick={() => addArrayItem("day_labour", { trade: "", number_of_persons: "", number_of: "", start_time: "07:00", end_time: "16:00", total_hours: "", activity_summary: "" })}
                >
                  <Plus className="w-3 h-3" />
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1">
                <div className="grid grid-cols-12 gap-1 text-xs font-semibold bg-slate-100 p-1">
                  <div className="col-span-3">COMPANY</div>
                  <div className="col-span-2">No. Of</div>
                  <div className="col-span-2">Start</div>
                  <div className="col-span-2">Finish</div>
                  <div className="col-span-2">HOURS</div>
                  <div className="col-span-1"></div>
                </div>
                {formData.day_labour.map((labour, index) => (
                  <div key={index} className="space-y-1">
                    <div className="grid grid-cols-12 gap-1 items-center">
                      <div className="col-span-3">
                        <Select 
                          value={labour.trade} 
                          onValueChange={(value) => {
                            updateArrayItem("day_labour", index, "trade", value);
                          }}
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue placeholder="Select Company" />
                          </SelectTrigger>
                          <SelectContent>
                            {companies.map(company => (
                              <SelectItem key={company.id} value={company.name} className="text-xs">
                                {company.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          value={labour.number_of}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateArrayItem("day_labour", index, "number_of", value);
                            updateArrayItem("day_labour", index, "number_of_persons", value);
                          }}
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="time"
                          value={labour.start_time}
                          onChange={(e) => handleDayLabourTimeChange(index, "start_time", e.target.value)}
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="time"
                          value={labour.end_time}
                          onChange={(e) => handleDayLabourTimeChange(index, "end_time", e.target.value)}
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          step="0.1"
                          value={labour.total_hours}
                          onChange={(e) => updateArrayItem("day_labour", index, "total_hours", e.target.value)}
                          className="h-7 text-xs"
                          readOnly
                        />
                      </div>
                      <div className="col-span-1">
                        {formData.day_labour.length > 0 && !isViewOnly && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => removeArrayItem("day_labour", index)}
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* Description row */}
                    <div className="grid grid-cols-12 gap-1">
                      <div className="col-span-12">
                        <Input
                          value={labour.activity_summary || ""}
                          onChange={(e) => updateArrayItem("day_labour", index, "activity_summary", e.target.value)}
                          className="h-7 text-xs"
                          placeholder="Activity description / notes"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Visitors */}
          <Card className="border-2 border-slate-700">
            <CardHeader className="bg-slate-700 text-white p-2 flex flex-row items-center justify-between">
              <CardTitle className="text-xs font-bold">VISITORS</CardTitle>
              {!isViewOnly && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 text-white hover:bg-slate-600"
                  onClick={() => addArrayItem("visitors", { name: "", company: "", purpose: "" })}
                >
                  <Plus className="w-3 h-3" />
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1">
                <div className="grid grid-cols-12 gap-1 text-xs font-semibold bg-slate-100 p-1">
                  <div className="col-span-5">NAME</div>
                  <div className="col-span-6">COMPANY</div>
                  <div className="col-span-1"></div>
                </div>
                {formData.visitors.map((visitor, index) => (
                  <div key={index} className="space-y-1">
                    <div className="grid grid-cols-12 gap-1 items-center">
                      <div className="col-span-5">
                        <Input
                          value={visitor.name}
                          onChange={(e) => updateArrayItem("visitors", index, "name", e.target.value)}
                          className="h-7 text-xs"
                          placeholder="Visitor name"
                        />
                      </div>
                      <div className="col-span-6">
                        <Input
                          value={visitor.company}
                          onChange={(e) => updateArrayItem("visitors", index, "company", e.target.value)}
                          className="h-7 text-xs"
                          placeholder="Company"
                        />
                      </div>
                      <div className="col-span-1">
                        {formData.visitors.length > 0 && !isViewOnly && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => removeArrayItem("visitors", index)}
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* Purpose row */}
                    <div className="grid grid-cols-12 gap-1">
                      <div className="col-span-12">
                        <Input
                          value={visitor.purpose || ""}
                          onChange={(e) => updateArrayItem("visitors", index, "purpose", e.target.value)}
                          className="h-7 text-xs"
                          placeholder="Purpose of visit"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

 
          {/* Significant Events */}
          <Card className="border-2 border-slate-700">
            <CardHeader className="bg-slate-700 text-white p-2">
              <CardTitle className="text-xs font-bold">SIGNIFICANT EVENTS</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <Textarea
                value={formData.significant_events}
                onChange={(e) => setFormData(prev => ({ ...prev, significant_events: e.target.value }))}
                className="text-xs min-h-[150px]"
                placeholder="List significant events, achievements, or issues for the day..."
              />
            </CardContent>
          </Card>
        </div>

        {/* Site Diary Photos — view only, scoped to selected site + date */}
        <DiaryPhotos
          siteId={formData.site_id}
          date={formData.date}
          siteName={sites.find((s) => s.id === formData.site_id)?.name}
        />

        {/* Control Buttons - Save, Delete, Print */}
        <div className="flex justify-center items-center gap-3 pt-4">
          {loadingRecord && (
            <div className="text-sm text-slate-600 flex items-center">
              <span className="animate-pulse">Loading existing record...</span>
            </div>
          )}
          
          {existingRecordId && (
            <>
              {!isViewOnly && (
                <Button
                  type="button"
                  onClick={handleDeleteRecord}
                  variant="destructive"
                  size="icon"
                  className="h-10 w-10"
                  title="Delete Record"
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
              )}
              
              <Button
                type="button"
                onClick={handlePrintPDF}
                variant="outline"
                className="px-6 py-2"
                title="Print to PDF"
              >
                <FileText className="w-4 h-4 mr-2" />
                Print PDF
              </Button>
            </>
          )}
          
          {!isViewOnly && (
            <Button
              type="submit"
              disabled={saving || loadingRecord}
              className="bg-slate-700 hover:bg-slate-800 text-white px-8 py-2"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : existingRecordId ? "Update Daily Record" : "Save Daily Record"}
            </Button>
          )}
        </div>
        </fieldset>
      </form>

      {/* Add Trade Dialog */}
      <Dialog open={showAddTradeDialog} onOpenChange={setShowAddTradeDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Add New Trade</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-trade" className="text-sm font-medium">
                Trade Name *
              </Label>
              <Input
                id="new-trade"
                placeholder="e.g., Carpenter, Electrician"
                value={newTradeName}
                onChange={(e) => setNewTradeName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTrade();
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddTradeDialog(false);
                setNewTradeName("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddTrade}
              disabled={addingItem || !newTradeName.trim()}
              className="bg-slate-700 hover:bg-slate-800"
            >
              {addingItem ? "Adding..." : "Add Trade"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Subcontractor Dialog */}
      <Dialog open={showAddCompanyDialog} onOpenChange={setShowAddCompanyDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Add New Subcontractor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-company" className="text-sm font-medium">
                Subcontractor Name *
              </Label>
              <Input
                id="new-company"
                placeholder="e.g., ABC Construction Ltd"
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCompany();
                  }
                }}
                autoFocus
              />
              <p className="text-xs text-slate-500">
                Will be allocated to the site selected above.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddCompanyDialog(false);
                setNewCompanyName("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddCompany}
              disabled={addingItem || !newCompanyName.trim()}
              className="bg-slate-700 hover:bg-slate-800"
            >
              {addingItem ? "Adding..." : "Add Subcontractor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Site Manager Dialog */}
      <Dialog open={showAddManagerDialog} onOpenChange={setShowAddManagerDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Add New Site Manager</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-manager-name" className="text-sm font-medium">
                Full Name *
              </Label>
              <Input
                id="new-manager-name"
                placeholder="e.g., John Smith"
                value={newManager.username}
                onChange={(e) => setNewManager(prev => ({ ...prev, username: e.target.value }))}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-manager-email" className="text-sm font-medium">
                Email Address *
              </Label>
              <Input
                id="new-manager-email"
                type="email"
                placeholder="e.g., john@construction.com"
                value={newManager.email}
                onChange={(e) => setNewManager(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-manager-password" className="text-sm font-medium">
                Password *
              </Label>
              <Input
                id="new-manager-password"
                type="password"
                placeholder="Minimum 6 characters"
                value={newManager.password}
                onChange={(e) => setNewManager(prev => ({ ...prev, password: e.target.value }))}
              />
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> The new site manager will be created with "site_manager" role.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddManagerDialog(false);
                setNewManager({ username: "", email: "", password: "" });
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddSiteManager}
              disabled={addingItem || !newManager.username.trim() || !newManager.email.trim() || !newManager.password.trim()}
              className="bg-slate-700 hover:bg-slate-800"
            >
              {addingItem ? "Adding..." : "Add Site Manager"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
